import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties
} from 'react'
import { FiCrosshair, FiMinimize2, FiMinus, FiPlus } from 'react-icons/fi'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import ScaleBadge from './ScaleBadge'

import * as S from './styles'

const viewportStyle: CSSProperties = {
  width: '100%',
  height: '100%'
}

// Bounds of react-zoom-pan-pinch use this node, not the <img>. Stretching it
// to 100% lets a high zoom pan the empty box until the creative leaves the stage.
const contentStyle: CSSProperties = {
  display: 'block',
  width: 'fit-content',
  height: 'fit-content'
}

const ResetZoomOnChange = ({
  resetKey,
  resetTransform
}: {
  resetKey?: CreativeZoomProps['resetKey']
  resetTransform: (animationTime?: number) => void
}) => {
  const previousKeyRef = useRef(resetKey)
  const resetTransformRef = useRef(resetTransform)
  resetTransformRef.current = resetTransform

  useEffect(() => {
    if (Object.is(previousKeyRef.current, resetKey)) {
      return
    }

    previousKeyRef.current = resetKey
    // Instant: a 200ms reset races the 300ms zoomIn animation. Opening
    // reject (or any resetKey change) should snap, not tween — otherwise
    // the badge can stick at an in-between scale (the 1.65× flake).
    resetTransformRef.current(0)
  }, [resetKey])

  return null
}

const CreativeZoom = ({ src, alt, resetKey }: CreativeZoomProps) => {
  const { t } = useAPPTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [renderedWidth, setRenderedWidth] = useState(0)
  const [naturalWidth, setNaturalWidth] = useState(0)

  const measureCssScale = useCallback(() => {
    const image = imageRef.current

    if (!image?.naturalWidth) {
      return
    }

    setRenderedWidth(image.clientWidth)
    setNaturalWidth(image.naturalWidth)
  }, [])

  useEffect(() => {
    setRenderedWidth(0)
    setNaturalWidth(0)

    if (imageRef.current?.complete) {
      measureCssScale()
    }
  }, [src, measureCssScale])

  useEffect(() => {
    const root = rootRef.current

    if (!root || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => measureCssScale())
    observer.observe(root)

    return () => observer.disconnect()
  }, [measureCssScale, src])

  return (
    <S.Root ref={rootRef} data-testid="creative-zoom">
      <TransformWrapper
        key={src}
        minScale={0.25}
        maxScale={8}
        centerOnInit
        limitToBounds
        centerZoomedOut
        disablePadding
      >
        {({ zoomIn, zoomOut, resetTransform, centerView }) => (
          <>
            <ResetZoomOnChange
              resetKey={resetKey}
              resetTransform={resetTransform}
            />
            <S.Controls data-testid="creative-zoom-controls">
              <S.ControlButton
                type="button"
                aria-label={t('c:actions.zoomOut')}
                title={t('c:actions.zoomOut')}
                onClick={() => zoomOut(0.5, 0)}
              >
                <FiMinus />
              </S.ControlButton>
              <ScaleBadge
                renderedWidth={renderedWidth}
                naturalWidth={naturalWidth}
              />
              <S.ControlButton
                type="button"
                aria-label={t('c:actions.zoomIn')}
                title={t('c:actions.zoomIn')}
                onClick={() => zoomIn(0.5, 0)}
              >
                <FiPlus />
              </S.ControlButton>
              <S.ControlButton
                type="button"
                aria-label={t('c:actions.collapse')}
                title={t('c:actions.collapse')}
                onClick={() => resetTransform(0)}
              >
                <FiMinimize2 />
              </S.ControlButton>
              <S.ControlButton
                type="button"
                aria-label={t('c:actions.center')}
                title={t('c:actions.center')}
                onClick={() => centerView()}
              >
                <FiCrosshair />
              </S.ControlButton>
            </S.Controls>

            <TransformComponent
              wrapperStyle={viewportStyle}
              contentStyle={contentStyle}
            >
              <S.Image
                ref={imageRef}
                data-testid="creative-zoom-image"
                src={src}
                alt={alt}
                onLoad={measureCssScale}
              />
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </S.Root>
  )
}

export default CreativeZoom
